/**
 * @file RepSnoozeMenu.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-16
 * @modified 2026-09-16
 *
 * The Snooze button on a Gone Quiet row, and the pop-up it opens: 1, 3, 6 or
 * 12 months, a specific date, or never. Picking one hands `until` (YYYY-MM-DD,
 * or null for never) to `onSnooze` and closes — there is no second "confirm",
 * because a snooze is cheap to undo from the Snoozed tab and a confirm on every
 * one would be a tax on the common case.
 *
 * Built on RowActionsMenu's look (its CSS classes, portal and dismissal
 * behaviour) rather than on that component itself, for two reasons:
 *
 *   - RowActionsMenu closes on every item click, and "Specific date" has to open
 *     a calendar inside the menu instead.
 *   - RowActionsMenu default-denies every item for a view-only account. That is
 *     right for row actions in general and wrong here: a snooze is the caller's
 *     own view preference, carved out for reps in middleware/readOnly.js, and
 *     reps are who this widget is for.
 *
 * For the same reason nothing here carries `data-write-action`.
 *
 * Unlike RowActionsMenu it keeps itself on screen: it is measured once it has
 * rendered, held inside the viewport on both axes, and opens upwards when there
 * is no room below — this card is often the last thing on a phone screen.
 */

import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import DateInput from '../components/DateInput';
import { showAlert } from '../components/Alert';
import { formatDateShort } from '../utils/dateFormatters';
import {
  SNOOZE_OPTIONS,
  londonTodayISO,
  addMonthsISO,
  snoozeUntilFor,
  isoToLocalDate
} from '../utils/snoozeDates';
import '../components/RowActionsMenu.css';
import './RepWidgets.css';

const EDGE = 8;   // closest the menu may sit to a viewport edge
const GAP = 4;    // between the trigger and the menu

const MONTH_OPTIONS = SNOOZE_OPTIONS.filter((o) => o.months);

function RepSnoozeMenu({ accountName, onSnooze, disabled = false, disabledTitle }) {
  const [open, setOpen] = useState(false);
  const [deployed, setDeployed] = useState(false);
  const [showDate, setShowDate] = useState(false);
  const [today, setToday] = useState(null);
  const [place, setPlace] = useState({ top: 0, right: EDGE, flipUp: false, maxHeight: null });

  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const closeTimer = useRef(null);

  const close = useCallback(() => {
    setDeployed(false);
    clearTimeout(closeTimer.current);
    // Let the fade finish before the portal unmounts, as RowActionsMenu does.
    closeTimer.current = setTimeout(() => {
      setOpen(false);
      setShowDate(false);
    }, 160);
  }, []);

  const toggle = (e) => {
    e.stopPropagation();
    if (open) { close(); return; }
    clearTimeout(closeTimer.current);
    // Read at the moment of opening, so a menu opened after midnight does not
    // offer yesterday's dates.
    setToday(londonTodayISO());
    setShowDate(false);
    setOpen(true);
  };

  /*
   * Position against the rendered size, not a guess. Right edge under the
   * trigger so it grows leftwards (the trigger is at the right of the row), then
   * pulled back inside the viewport; below the trigger unless that runs off the
   * bottom and above has room. offsetWidth/offsetHeight rather than
   * getBoundingClientRect: the opening animation scales the menu, and layout
   * size is what has to fit.
   */
  const measure = useCallback(() => {
    const trigger = triggerRef.current;
    const menu = menuRef.current;
    if (!trigger || !menu) return;

    const r = trigger.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = menu.offsetWidth;
    const h = Math.min(menu.scrollHeight, vh - EDGE * 2);

    const right = Math.min(Math.max(EDGE, vw - r.right), Math.max(EDGE, vw - w - EDGE));
    const below = r.bottom + GAP;
    const fitsBelow = below + h <= vh - EDGE;
    const fitsAbove = r.top - GAP - h >= EDGE;

    const flipUp = !fitsBelow && fitsAbove;
    const top = flipUp
      ? r.top - GAP - h
      : Math.max(EDGE, Math.min(below, vh - EDGE - h));

    setPlace({ top, right, flipUp, maxHeight: vh - EDGE * 2 });
  }, []);

  // Re-measured when the calendar opens or closes, since that changes the height.
  useLayoutEffect(() => {
    if (!open) return;
    measure();
  }, [open, showDate, measure]);

  // Deploy on the frame after the first placement, so it animates from the
  // right spot rather than from the corner of the screen.
  useEffect(() => {
    if (!open) return undefined;
    const raf = requestAnimationFrame(() => setDeployed(true));
    return () => cancelAnimationFrame(raf);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const inside = (target) =>
      (menuRef.current && menuRef.current.contains(target)) ||
      (triggerRef.current && triggerRef.current.contains(target));

    const onPointer = (e) => { if (!inside(e.target)) close(); };
    // The page scrolling moves the trigger out from under a fixed menu; the
    // menu's own overflow scrolling (a tall calendar on a short phone) must not
    // count as that.
    const onScroll = (e) => { if (!inside(e.target)) close(); };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        close();
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('scroll', onScroll, true);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('scroll', onScroll, true);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', close);
    };
  }, [open, close]);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const choose = (key, specific) => {
    let until;
    try {
      until = snoozeUntilFor(key, today || londonTodayISO(), specific);
    } catch (err) {
      showAlert(err.message, 'warning');
      return;
    }
    close();
    onSnooze(until);
  };

  const shortDate = (iso) => formatDateShort(`${iso}T00:00:00`);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`btn-icon row-actions-trigger${open ? ' open' : ''}`}
        onClick={toggle}
        disabled={disabled}
        title={disabled ? (disabledTitle || 'Cannot snooze this') : `Snooze ${accountName}`}
        aria-label={`Snooze ${accountName}`}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="material-symbols-rounded">snooze</span>
      </button>

      {open && today && ReactDOM.createPortal(
        <div
          ref={menuRef}
          className={`row-actions-menu rep-snooze-menu${deployed ? ' deployed' : ''}${place.flipUp ? ' flip-up' : ''}`}
          style={{ top: place.top, right: place.right, maxHeight: place.maxHeight || undefined }}
          role="menu"
          aria-label={`Snooze ${accountName}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="rep-snooze-head">
            <span className="rep-snooze-title">Snooze {accountName}</span>
            {/* Said out loud because the obvious worry is hiding it from the
                office — it does not. */}
            <span className="rep-snooze-hint">Hides it from your Gone Quiet only</span>
          </div>

          {MONTH_OPTIONS.map((o) => (
            <button
              key={o.key}
              type="button"
              role="menuitem"
              className="row-actions-item"
              onClick={() => choose(o.key)}
            >
              <span className="material-symbols-rounded">event_upcoming</span>
              <span className="row-actions-label">{o.label}</span>
              <span className="rep-snooze-when">{shortDate(addMonthsISO(today, o.months))}</span>
            </button>
          ))}

          <button
            type="button"
            role="menuitem"
            className={`row-actions-item${showDate ? ' active' : ''}`}
            aria-expanded={showDate}
            onClick={() => setShowDate((v) => !v)}
          >
            <span className="material-symbols-rounded">calendar_month</span>
            <span className="row-actions-label">Specific date</span>
            <span className="material-symbols-rounded rep-snooze-chevron">
              {showDate ? 'expand_less' : 'expand_more'}
            </span>
          </button>

          {showDate && (
            <div className="rep-snooze-date">
              {/* Inline, so the calendar lives inside the menu rather than in a
                  second popper the outside-click handler would treat as
                  "elsewhere". Picking a day saves; past days are disabled. */}
              <DateInput
                inline
                value=""
                minDate={isoToLocalDate(today)}
                onChange={(e) => { if (e.target.value) choose('date', e.target.value); }}
              />
            </div>
          )}

          <button
            type="button"
            role="menuitem"
            className="row-actions-item"
            onClick={() => choose('never')}
          >
            <span className="material-symbols-rounded">notifications_off</span>
            <span className="row-actions-label">Never</span>
            <span className="rep-snooze-when">until you unsnooze</span>
          </button>
        </div>,
        document.body
      )}
    </>
  );
}

export default RepSnoozeMenu;
